"use client";

import { useTheme } from "next-themes";
import { motion } from "framer-motion";
import {
  ArrowGlobe,
  Globe,
  GlobeBox,
  ShieldGlobe,
  WindowGlobe,
} from "geist-icons";
import { ComponentProps, FC } from "react";

interface GeistIconProps extends ComponentProps<any> {
  size?: number;
}

interface CircleProps {
  size: number;
  position: { x: number; y: number };
  icon: FC<GeistIconProps>;
  iconSize?: number;
  startAngle?: number;
}

const CircleSkeleton = ({
  size,
  position,
}: {
  size: number;
  position: { x: number; y: number };
}) => {
  return (
    <div
      className="animate-pulse bg-neutral-200 dark:bg-neutral-800 rounded-full border border-neutral-300 dark:border-neutral-700"
      style={{
        position: "absolute",
        width: `${size}px`,
        height: `${size}px`,
        left: `${position.x * 100}%`,
        top: `${position.y * 100}%`,
        transform: "translate(-50%, -50%)",
        zIndex: 2,
      }}
    />
  );
};

export const Circle = ({
  size,
  position,
  icon: Icon,
  iconSize = 24,
  startAngle = 0,
}: CircleProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const beamColor = isDark ? "#ffffff" : "#000000";
  const strokeIconColor = isDark ? "#363636" : "#EEEEEE";
  const strokeColor = isDark ? "#121212" : "#EBEBEB";
  const iconColor = isDark ? "#ffffff" : "#000000";
  const bgColor = isDark ? "#0A0A0A" : "#ffffff";

  const outerRadius = 50;
  const innerRadius = 35;
  const beamLength = 0.5;

  const outerCirc = 2 * Math.PI * outerRadius;
  const innerCirc = 2 * Math.PI * innerRadius;

  const outerDash = outerCirc * beamLength;
  const innerDash = innerCirc * beamLength;

  const iconWrapperSize = 38;

  const centerX = position.x * 100;
  const centerY = position.y * 100;

  if (!resolvedTheme) {
    return <CircleSkeleton size={size} position={position} />;
  }

  const gradientId = `beamGradient-${resolvedTheme}`;

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      style={{
        position: "absolute",
        width: `${size}px`,
        height: `${size}px`,
        left: `${position.x * 100}%`,
        top: `${position.y * 100}%`,
        transform: "translate(-50%, -50%)",
        overflow: "visible",
        zIndex: 2,
      }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="1">
          <stop offset="0%" stopColor={beamColor} stopOpacity="0" />
          <stop offset="40%" stopColor={beamColor} stopOpacity="0.8" />
          <stop offset="50%" stopColor={beamColor} stopOpacity="1" />
          <stop offset="60%" stopColor={beamColor} stopOpacity="0.8" />
          <stop offset="100%" stopColor={beamColor} stopOpacity="0" />
        </linearGradient>
        <filter id="blurFilter">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" />
        </filter>
      </defs>

      {/* Outer static circle */}
      <circle
        cx="50"
        cy="50"
        r={outerRadius}
        stroke={strokeColor}
        strokeWidth="1"
        fill="none"
      />

      {/* Inner static circle */}
      <circle
        cx="50"
        cy="50"
        r={innerRadius}
        stroke={strokeColor}
        strokeWidth="1"
        fill="none"
      />

      {/* Animated outer beam */}
      <motion.circle
        cx="50"
        cy="50"
        r={outerRadius}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="2"
        strokeDasharray={`${outerDash} ${outerCirc}`}
        strokeLinecap="round"
        initial={{ rotate: startAngle }}
        animate={{ rotate: startAngle + 360 }}
        transition={{ duration: 3, ease: "linear", repeat: Infinity }}
        style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }}
        filter="url(#blurFilter)"
      />

      {/* Animated inner beam */}
      <motion.circle
        cx="50"
        cy="50"
        r={innerRadius}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="2"
        strokeDasharray={`${innerDash} ${innerCirc}`}
        strokeLinecap="round"
        initial={{ rotate: startAngle }}
        animate={{ rotate: startAngle + 360 }}
        transition={{ duration: 3, ease: "linear", repeat: Infinity }}
        style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }}
        filter="url(#blurFilter)"
      />

      {/* Icon circle */}
      <foreignObject
        x={50 - iconWrapperSize / 2}
        y={50 - iconWrapperSize / 2}
        width={iconWrapperSize}
        height={iconWrapperSize}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: bgColor,
            borderRadius: "50%",
            border: `1px solid ${strokeIconColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={iconSize} color={iconColor} />
        </div>
      </foreignObject>
    </svg>
  );
};

export default function AnimatedCircularShapes() {
  return (
    <div className="relative w-full h-[470px] md:h-[610px] md:bottom-2 lg:bottom-0 lg:h-[500px]">
      <Circle
        size={170}
        position={{ x: 0.5, y: 0.3 }}
        icon={Globe as FC<GeistIconProps>}
        startAngle={0}
      />
      <Circle
        size={140}
        position={{ x: 0.095, y: 0.06 }}
        icon={ArrowGlobe as FC<GeistIconProps>}
        startAngle={60}
      />
      <Circle
        size={140}
        position={{ x: 0.9, y: 0.06 }}
        icon={WindowGlobe as FC<GeistIconProps>}
        startAngle={120}
      />
      <Circle
        size={140}
        position={{ x: 0.095, y: 0.55 }}
        icon={GlobeBox as FC<GeistIconProps>}
        startAngle={180}
      />
      <Circle
        size={140}
        position={{ x: 0.9, y: 0.55 }}
        icon={ShieldGlobe as FC<GeistIconProps>}
        startAngle={240}
      />
    </div>
  );
}
